import {useLocation} from 'react-router-dom';
import {Header} from '../components/Header';
import {TmsTaskCardItem} from '../components/TmsTaskCardItem';
import {t} from '../../../i18n';
import {TmsPageState} from '../types/tms';
import {HandleScanEventResult, VehicleRelatedScanComponent} from '../components/VehicleRelatedScanComponent';
import {UnsealingInteriorPhotoForm} from './components/UnsealingInteriorPhotoForm';
import {parseException} from "../../../utils/exceptionUtils";
import {Button, Dialog, Toast} from "antd-mobile";
import {gql, useMutation} from "@apollo/client";
import {getBottomNavBarContainerStyle} from "../../../utils/deviceUtils";
import {Pda} from "../components/Pda";
import { useNavigate } from "react-router-dom";
import {PdaPath} from "../../../enums/PdaNavigatePathEnum";
import { TmsOperationSourceEnum, TmsTaskWaypointNodeType } from '../../../enums/TmsEnum';
import { useSaveTmsTaskWaypointLocationInfo } from './hooks/useSaveTmsTaskWaypointLocationInfo';
import { FirstPhotoWatermarkLocation } from '../../../utils/appRuntime';
import {useEffect, useRef, useState} from "react";
import {fileUpload} from "../../../utils/storageUtils";
import {base64ToFile} from "../../../services/DeliveryDataService";

const HANDLE_TMS_WHS_UNSEALING_SCAN_EVENT = gql`
    mutation TmsWarehouseOperationsMutation($input: HandleTmsWhsUnsealingScanEventInput!) {
        handleTmsWhsUnsealingScanEvent(input: $input) {
            success
            message
            __typename
        }
    }
`

const SUBMIT_TMS_WHS_UNSEALING_SCAN_EVENT = gql`
    mutation SubmitTmsWhsUnsealingScanEvent($input: HandleTmsWhsUnsealingScanEventInput!) {
        submitTmsWhsUnsealingScanEvent(input: $input) {
            success
            message
            __typename
        }
    }
`

interface HandleTmsWhsUnsealingScanEventInput {
    taskWayPointId: number;
    sealNumber: string;
    taskId: number;
}

interface HandleTmsWhsUnsealingScanEventResponse {
    handleTmsWhsUnsealingScanEvent: {
        success: boolean;
        message: string;
        __typename: string;
    };
}

interface SubmitTmsWhsUnsealingScanEventResponse {
    submitTmsWhsUnsealingScanEvent: {
        success: boolean;
        message: string;
        __typename: string;
    };
}


interface PageBodyProps {
    onScanSuccess: (sealNumber: string) => void;
    interiorPhotos: string[];
    onInteriorPhotosChange: (photos: string[]) => void;
    onFirstPhotoLocationChange?: (loc: FirstPhotoWatermarkLocation | null) => void;
}

const PageBody: React.FC<PageBodyProps> = ({ onScanSuccess, interiorPhotos, onInteriorPhotosChange, onFirstPhotoLocationChange }) => {
    const [handleTmsWhsUnsealingScanEvent] = useMutation<HandleTmsWhsUnsealingScanEventResponse>(HANDLE_TMS_WHS_UNSEALING_SCAN_EVENT);
    const location = useLocation();
    const { ...taskData } = location.state as TmsPageState;

    const handleScanEvent = async (value1: string, value2: string): Promise<HandleScanEventResult> => {
        if (value2 === "") {
            return {success: false, message: t('pda.common.sealNumberRequired') || '封车号必填'};
        }

        try {
            const {data} = await handleTmsWhsUnsealingScanEvent({
                variables: {
                    input: {
                        taskWayPointId: taskData.taskWaypointId,
                        sealNumber: value2,
                        taskId: taskData.taskId,
                    },
                },
            });

            const success = data?.handleTmsWhsUnsealingScanEvent?.success || false;
            const responseMessage = data?.handleTmsWhsUnsealingScanEvent?.message || '';

            if (!success) {
                return {success: false, message: responseMessage || t('pda.common.scanFailed') || '扫描失败'};
            }

            // 校验通过，将 sealNumber 传给父组件暂存，待上传照片后一并提交
            onScanSuccess(value2);
            return {success: true};
        } catch (error) {
            const {message} = parseException(error);
            await Dialog.alert({
                content: (
                    <div style={{backgroundColor: '#ffffff', padding: '16px'}}>
                        <div className="text-base text-center text-gray-800">
                            {message}
                        </div>
                    </div>
                ),
                confirmText: t('pda.common.cancel'),
            });

            return {success: false, message: message};
        }
    };

    return (
        <div className="flex flex-col gap-2">
            {taskData?.task && <TmsTaskCardItem task={taskData.task} showButtons={false}/>}
            <VehicleRelatedScanComponent
                value1Placeholder={t('pda.pages.unsealing.licensePlate')}
                value2Placeholder={t('pda.pages.unsealing.sealNO.')}
                value1ColumnTitle={t('pda.pages.unsealing.licensePlate')}
                value2ColumnTitle={t('pda.pages.unsealing.sealNO.')}
                isCheckBarCodeValid={false}
                handleScanEvent={handleScanEvent}
                hideValue1Input
                isValue1Required={false}
            />
            <UnsealingInteriorPhotoForm
                photos={interiorPhotos}
                onPhotosChange={onInteriorPhotosChange}
                onFirstPhotoLocationChange={onFirstPhotoLocationChange}
            />
        </div>
    );
}

export default function TmsUnsealingPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const {backRoute, ...taskData} = location.state as TmsPageState;

    const buttonContainerRef = useRef<HTMLDivElement>(null);
    const focusTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const lastViewportHeightRef = useRef<number>(window.visualViewport?.height || window.innerHeight);
    const [scanSuccess, setScanSuccess] = useState(false);
    const [scannedSealNumber, setScannedSealNumber] = useState<string>('');
    const [interiorPhotos, setInteriorPhotos] = useState<string[]>([]);
    const [firstPhotoLocation, setFirstPhotoLocation] = useState<FirstPhotoWatermarkLocation | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const canComplete = scanSuccess && interiorPhotos.length >= 1;
    const [submitTmsWhsUnsealingScanEvent] = useMutation<SubmitTmsWhsUnsealingScanEventResponse>(SUBMIT_TMS_WHS_UNSEALING_SCAN_EVENT);
    const { save: saveTmsTaskWaypointLocationInfo } = useSaveTmsTaskWaypointLocationInfo();

    const handleScanSuccess = (sealNumber: string) => {
        setScanSuccess(true);
        setScannedSealNumber(sealNumber);
    };

    const handleComplete = async () => {
        if (!canComplete || isSubmitting) {
            return;
        }

        setIsSubmitting(true);
        try {
            const taskId = Number(taskData.taskId);
            const taskWaypointId = Number(taskData.taskWaypointId);
            if (!Number.isFinite(taskId) || !Number.isFinite(taskWaypointId)) {
                throw new Error(t('pda.pages.tms.departure.invalidTaskId') || 'Invalid task ID or waypoint ID');
            }

            const uploadPromises = interiorPhotos.map(async (base64Photo) => {
                const formattedBase64 = base64Photo.startsWith('data:') ? base64Photo : `data:image/jpeg;base64,${base64Photo}`;
                const file = await base64ToFile(formattedBase64, `unseal_interior_${Date.now()}_${Math.random().toString(36).substring(7)}.jpg`);
                const result = await fileUpload(file, 'tms_unseal_interior_photo');
                return result.objectKey;
            });

            const photoKeys = await Promise.all(uploadPromises);

            await saveTmsTaskWaypointLocationInfo(taskId, taskWaypointId, TmsTaskWaypointNodeType.UNSEAL, firstPhotoLocation);

            // 照片上传后，合并提交解封车扫描结果 + 车内照片
            const submitResult = await submitTmsWhsUnsealingScanEvent({
                variables: {
                    input: {
                        taskWayPointId: taskWaypointId,
                        sealNumber: scannedSealNumber,
                        taskId: taskId,
                        unSealInteriorPhotosJson: photoKeys,
                        operationSource: TmsOperationSourceEnum.PDA,
                    }
                }
            });

            const submitSuccess = submitResult.data?.submitTmsWhsUnsealingScanEvent?.success || false;
            if (!submitSuccess) {
                const submitMessage = submitResult.data?.submitTmsWhsUnsealingScanEvent?.message || '';
                throw new Error(submitMessage || t('pda.common.submitFailed') || 'Submit failed');
            }

            Toast.show({
                content: t('pda.common.submitSuccess') || 'Submit successful',
                position: 'center',
                duration: 2000
            });

            navigate(PdaPath.PDA_SHIP_IN ,{ state: { backRoute, ...taskData } });
        } catch (error) {
            console.error('解封车提交失败:', error);
            const errorMessage = error instanceof Error ? error.message : String(error);
            Toast.show({
                content: `${t('pda.common.submitFailed') || 'Submit failed'}${errorMessage ? `: ${errorMessage}` : ''}`,
                position: 'center',
                duration: 2000
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // 监听键盘显示/隐藏 - 直接操作 DOM，不触发 React 重新渲染
    useEffect(() => {
        const handleFocusIn = (e: FocusEvent) => {
            const target = e.target as HTMLElement;
            // 只在非按钮元素获得焦点时隐藏按钮
            if (target && !target.closest('button') && buttonContainerRef.current) {
                // 清除之前的超时
                if (focusTimeoutRef.current) {
                    clearTimeout(focusTimeoutRef.current);
                }
                // 延迟一小段时间，确保焦点真正停留在输入框上（避免快速切换导致的问题）
                focusTimeoutRef.current = setTimeout(() => {
                    if (buttonContainerRef.current) {
                        buttonContainerRef.current.style.opacity = '0';
                        buttonContainerRef.current.style.pointerEvents = 'none';
                    }
                }, 50);
            }
        };

        const handleFocusOut = () => {
            // 清除之前的超时
            if (focusTimeoutRef.current) {
                clearTimeout(focusTimeoutRef.current);
            }
            if (buttonContainerRef.current) {
                // 直接显示按钮
                buttonContainerRef.current.style.opacity = '1';
                buttonContainerRef.current.style.pointerEvents = 'auto';
            }
        };

        // 监听 visualViewport 变化，检测虚拟键盘的显示/隐藏
        const handleViewportChange = () => {
            const currentHeight = window.visualViewport?.height || window.innerHeight;
            const lastHeight = lastViewportHeightRef.current;

            // 如果高度增加了，说明键盘消失了
            if (currentHeight > lastHeight && buttonContainerRef.current) {
                // 确保按钮显示
                buttonContainerRef.current.style.opacity = '1';
                buttonContainerRef.current.style.pointerEvents = 'auto';
            }
            // 如果高度减少了，说明键盘显示了，检查焦点
            else if (currentHeight < lastHeight) {
                const activeElement = document.activeElement as HTMLElement;
                if (activeElement && !activeElement.closest('button') && buttonContainerRef.current) {
                    buttonContainerRef.current.style.opacity = '0';
                    buttonContainerRef.current.style.pointerEvents = 'none';
                }
            }

            lastViewportHeightRef.current = currentHeight;
        };

        window.addEventListener('focusin', handleFocusIn);
        window.addEventListener('focusout', handleFocusOut);
        window.visualViewport?.addEventListener('resize', handleViewportChange);

        return () => {
            window.removeEventListener('focusin', handleFocusIn);
            window.removeEventListener('focusout', handleFocusOut);
            window.visualViewport?.removeEventListener('resize', handleViewportChange);
            if (focusTimeoutRef.current) {
                clearTimeout(focusTimeoutRef.current);
            }
        };
    }, []);

    if (!taskData) {
        return null;
    }

    return (
        <div className="w-full h-full flex flex-col">
            <div className="flex flex-col gap-2 p-2 flex-1 overflow-y-auto pb-20 bg-gray-50">
                <Header title={t('pda.pages.shipOut.unsealing')} backRoute={backRoute}/>
                <Pda/>
                <PageBody
                    onScanSuccess={handleScanSuccess}
                    interiorPhotos={interiorPhotos}
                    onInteriorPhotosChange={setInteriorPhotos}
                    onFirstPhotoLocationChange={setFirstPhotoLocation}
                />
            </div>
            {/* 固定在底部的按钮 - 使用sticky替代fixed防止被虚拟键盘顶起 */}
            <div
                ref={buttonContainerRef}
                className={`${getBottomNavBarContainerStyle('fixed bottom-0 left-0 w-full p-2 bg-white border-t border-gray-200 z-20').className} transition-opacity duration-300`}
                style={{
                    ...getBottomNavBarContainerStyle('fixed bottom-0 left-0 w-full p-2 bg-white border-t border-gray-200 z-20').style,
                    willChange: 'opacity'
                }}>
                <div className="flex gap-2">
                    <Button
                        block
                        color="primary"
                        disabled={!canComplete || isSubmitting}
                        onClick={handleComplete}
                        style={{flex: 1}}
                    >
                        {t('pda.pages.tms.arrival.complete') || t('pda.common.complete') || '完成'}
                    </Button>
                </div>
            </div>
        </div>
    );
}
